import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, TextField, Typography } from "@mui/material"
import PaperDraggable from "../../../../../../../../../../components/PaperDraggable"
import { useFormContext } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatarDecimalNumber, formatarInputValorMonetárioSemPrefixo, formatarMonetárioParaNúmero } from "../../../../../../../../../../helpers/masks";
import { TBancos } from "../../../../../../../../../../types/api/financeiro/bancos";
import { enqueueSnackbar } from "notistack";
import CartaoForm from "./CartaoForm";
import ChequeForm from "./ChequeForm";
import SelectFormaPagamento, { TObjValueFormPagamento } from "../../../../../../../../../../components/SelectsSimpleApiQuery/SelectFormaPagamento";

export default function DialogCadastrarPag({
    open,
    value,
    handleClose
}: DialogCadastrarPagProps) {

    const { setValue, getValues } = useFormContext();

    const defValueFormCheque = {
        cheque_banco: null,
        cheque_num: '',
    }

    const defValueFormCartao = {
        cartao_nsu: '',
        cartao_autorizacao: ''
    }

    const editModalMode = useMemo(() => value !== null, [value])

    const valorRestante = useMemo(() => {
        const totalAtt = getValues('total')
        const valoresRec = getValues('finalizacao.pagamentos')

        let totalValores = 0
        if (valoresRec.length > 0) {
            totalValores = valoresRec.reduce((acumulador: number, item: any) =>
                acumulador + formatarMonetárioParaNúmero(item.valor), 0
            );
        }

        return totalAtt - totalValores
    }, [getValues])

    const [internForm, setInternForm] = useState<IInternForm>({
        formaPagamento: null,
        valor: formatarDecimalNumber(valorRestante),
    })

    //funções manipuladoras
    const handleChangeValues = (prop: keyof IInternForm, value: any) => {
        setInternForm(prevState => {
            if (prop === "formaPagamento" && value) {
                let defForm = {}
                if (value.requerNsu) defForm = defValueFormCartao
                if (value.requerChequeInfo) defForm = defValueFormCheque

                const {
                    cartao_nsu,
                    cartao_autorizacao,
                    cheque_num,
                    cheque_banco,
                    ...newState
                } = prevState

                return ({
                    ...newState,
                    ...defForm,
                    [prop]: value
                })
            }

            return ({
                ...prevState,
                [prop]: value
            })
        })
    }

    const handleSubmitForm = () => {
        const params = internForm;
        const arrRecb = getValues('finalizacao.pagamentos');

        if (internForm.formaPagamento?.requerChequeInfo && (!internForm.cheque_banco || !internForm.cheque_num)) {
            enqueueSnackbar("Os campos Banco e Nº cheque são obrigatórios", { variant: "warning" })
            return
        }

        if (editModalMode) arrRecb.splice(value, 1, params);
        if (!editModalMode) arrRecb.push(params);

        setValue('finalizacao.pagamentos', arrRecb);
        handleClose();
    }

    const verificarCamposForm = useCallback((chave: "cheque" | "cartao") => {
        const keys = Object.keys(internForm) as Array<keyof IInternForm>;

        const formKeys = keys.filter(key => key.startsWith(chave));

        if (formKeys.length === 0) return false;

        const todosValidos = formKeys.every(key => {
            const valor = internForm[key];
            return valor !== '' && valor !== null && valor !== undefined;
        });
        return todosValidos;
    }, [internForm]);
    //

    //valores importantes
    const btnDisabled = useMemo(() => {
        let verificacaoForm = true
        const camposBaseFilled = Boolean(internForm.formaPagamento) && formatarMonetárioParaNúmero(internForm.valor) > 0;

        if (internForm.formaPagamento?.requerNsu) {
            verificacaoForm = verificarCamposForm("cartao")
        }

        if (internForm.formaPagamento?.requerChequeInfo) {
            verificacaoForm = verificarCamposForm("cheque")
        }

        return !(camposBaseFilled && verificacaoForm)
    }, [internForm, verificarCamposForm])
    //

    useEffect(() => {
        if (value !== null) {
            const formValues = getValues('finalizacao.pagamentos')
            setInternForm(formValues[value])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            scroll="body"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                Cadastrar valor de pagamento
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} my={1}>
                    <Grid size={{xs: 12, md: 6}}>
                        
                        <SelectFormaPagamento
                            value={internForm.formaPagamento}
                            onChange={(value) => {
                                handleChangeValues("formaPagamento", value)
                            }}
                            loja_id={getValues('finalizacao.loja')?.id}
                            flg_usa_pagar
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label='Valor pago'
                            type="text"
                            value={internForm.valor}
                            onChange={(e) => {
                                const formattedValue = formatarInputValorMonetárioSemPrefixo(e.target.value)
                                handleChangeValues("valor", formattedValue)
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography color={'#000'}>R$</Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                    </Grid>

                    {
                        internForm.formaPagamento?.requerNsu &&
                        <CartaoForm {...{ handleChangeValues }} values={internForm} />
                    }

                    {
                        internForm.formaPagamento?.requerChequeInfo &&
                        <ChequeForm {...{ handleChangeValues }} values={internForm} />
                    }
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmitForm}
                    disabled={btnDisabled}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogCadastrarPagProps {
    open: boolean,
    value: number | null,
    handleClose: () => void
}

export interface IInternForm {
    formaPagamento: TObjValueFormPagamento | null,
    valor: string,

    //cartão
    cartao_nsu?: string,
    cartao_autorizacao?: string

    //cheque
    cheque_banco?: TBancos | null,
    cheque_num?: string,
}
